import { EnvelopeIcon, PhoneIcon, ShoppingBagIcon, CurrencyDollarIcon, ChatBubbleLeftRightIcon, StarIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../../components';
import { formatCurrency, formatDate } from '../../../lib/utils';
import { useAuthStore } from '../../../stores';
import type { CustomerDetail } from '../services/customerService';

interface CustomerProfileProps {
  customer: CustomerDetail;
  /** Service-business stores (e.g. Clerk) hide order/lifetime-value tiles. */
  showCommerceMetrics?: boolean;
}

/**
 * Format an attribute key into a human-readable label.
 * e.g. "avg_order_value" -> "Avg Order Value"
 */
function formatAttrLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format an attribute value for display.
 */
function formatAttrValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return formatDate(value);
  return String(value);
}

export default function CustomerProfile({
  customer,
  showCommerceMetrics = true,
}: CustomerProfileProps) {
  const currency = useAuthStore((s) => s.currency);

  // Collect dynamic attributes
  const attributes = (customer as any).attributes || {};
  const displayName = customer.name || customer.customerName || 'Unknown';
  const phone = customer.phoneNumber || customer.whatsappNumber || '';

  // Determine meaningful stats to show based on what's available. On
  // service-business stores, suppress the e-commerce tiles (orders / lifetime
  // value) — that context comes from the business integration instead.
  const hasOrders = showCommerceMetrics && customer.totalOrders > 0;
  const hasLifetimeValue = showCommerceMetrics && customer.lifetimeValue > 0;
  const hasInteractions = customer.totalInteractions > 0;

  // Filter out internal/system attributes for display
  const displayAttributes = Object.entries(attributes).filter(
    ([key, value]) =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !key.startsWith('_') &&
      key !== 'avg_order_value' &&
      key !== 'last_order_date'
  );

  return (
    <div>
      <h4 className="mb-3 text-sm font-semibold text-ink">Profile</h4>

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Avatar name={displayName} size="lg" />
          <div>
            <p className="font-medium text-ink">{displayName}</p>
            <p className="text-sm text-ink-3">
              Since {formatDate(customer.createdAt)}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {customer.email && (
            <div className="flex items-center gap-2 text-sm">
              <EnvelopeIcon className="h-4 w-4 text-ink-2" />
              <span className="text-ink-2">{customer.email}</span>
            </div>
          )}
          {phone && (
            <div className="flex items-center gap-2 text-sm">
              <PhoneIcon className="h-4 w-4 text-ink-2" />
              <span className="text-ink-2">{phone}</span>
            </div>
          )}
        </div>

        {/* Key metrics grid — adapts based on available data */}
        <div className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface-2 p-3">
          {hasOrders && (
            <div>
              <div className="flex items-center gap-1.5 text-ink-2">
                <ShoppingBagIcon className="h-4 w-4" />
                <span className="text-xs">Orders</span>
              </div>
              <p className="mt-1 text-lg font-semibold text-ink">
                {customer.totalOrders}
              </p>
            </div>
          )}
          {!hasOrders && hasInteractions && (
            <div>
              <div className="flex items-center gap-1.5 text-ink-2">
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                <span className="text-xs">Interactions</span>
              </div>
              <p className="mt-1 text-lg font-semibold text-ink">
                {customer.totalInteractions}
              </p>
            </div>
          )}
          {hasLifetimeValue && (
            <div>
              <div className="flex items-center gap-1.5 text-ink-2">
                <CurrencyDollarIcon className="h-4 w-4" />
                <span className="text-xs">Lifetime</span>
              </div>
              <p className="mt-1 text-lg font-semibold text-ink">
                {formatCurrency(customer.lifetimeValue, currency)}
              </p>
            </div>
          )}
          {customer.satisfactionScore != null && (
            <div>
              <div className="flex items-center gap-1.5 text-ink-2">
                <StarIcon className="h-4 w-4" />
                <span className="text-xs">Satisfaction</span>
              </div>
              <p className="mt-1 text-lg font-semibold text-ink">
                {Number(customer.satisfactionScore).toFixed(1)}/5
              </p>
            </div>
          )}
          {!hasOrders && !hasInteractions && !hasLifetimeValue && (
            <div className="col-span-2 text-center text-sm text-ink-3">
              No interaction data yet
            </div>
          )}
        </div>

        {/* Dynamic attributes — renders business-specific data */}
        {displayAttributes.length > 0 && (
          <div className="space-y-2">
            <h5 className="text-xs font-medium uppercase text-ink-3">Details</h5>
            <div className="space-y-1.5">
              {displayAttributes.map(([key, value]) => (
                <div key={key} className="flex items-center justify-between text-sm">
                  <span className="text-ink-3">{formatAttrLabel(key)}</span>
                  <span className="text-ink-2">{formatAttrValue(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
